import React, { useState } from 'react';
import { View, Pressable } from 'react-native-web';
import { useLocation, useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import CustomText from '../../components/atoms/CustomText';
import { colors, spacing, typography } from '../../utils/styling';
import { useResponsive } from '../../hooks/useResponsive';
import { useUpdateLeadWithPriorities } from '../../lib/quizzes';

// icons
import SvgChevron from '../../components/icons/SvgChevron';
import SvgPiggy from '../../components/icons/SvgPiggy';
import SvgPalmTree from '../../components/icons/SvgPalmtree';
import SvgKeys from '../../components/icons/SvgKeys';
import SvgDrawingCompass from '../../components/icons/SvgDrawingCompass';
import SvgWreath from '../../components/icons/SvgWreath';
import SvgTools from '../../components/icons/SvgTools';

interface SpecificResultsData {
  answers: number[];
  specificCategoryScores: Record<string, number>;
  dominantSpecificCategory: string;
  clientId?: number;
  sfAccount?: string | null;
  quizId?: number;
  leadId?: number;
  mainResultsData?: {
    answers: number[];
    categoryScores: Record<string, number>;
    dominantCategory: string;
    clientId?: number;
    sfAccount?: string | null;
    quizId?: number;
  };
}

const priorities = [
  { name: 'Retirement planning', icon: SvgPalmTree }, 
  { name: 'Estate planning', icon: SvgKeys }, 
  { name: 'Investments', icon: SvgPiggy },         
  { name: 'Tax planning & prep', icon: SvgDrawingCompass }, 
  { name: 'Life insurance', icon: SvgWreath },     
  { name: 'Other', icon: SvgTools },                // tools
];

export default function SubmitPriorities() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const { mutateAsync: updateLeadWithPriorities } = useUpdateLeadWithPriorities();
  const [hasSubmit, setHasSubmit] = useState(false);
  const resultsData = location.state as SpecificResultsData;

  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [otherText, setOtherText] = useState('');

  const { quizId } = resultsData;
  if (!quizId) return null;

  const handleBack = () => {
    navigate('/quiz/connect/confirm', { state: { ...resultsData, from: location.pathname } });
  };

  const handleReturn = () => {
    navigate('/quiz/specific/results', { state: { ...resultsData, from: location.pathname } });
  }

  const handleSelectPriority = (priority: string) => {
    setSelectedPriorities(prev =>
      prev.includes(priority)
        ? prev.filter(p => p !== priority)
        : [...prev, priority]
    );
  };

  const handleSubmit = async () => {
    setHasSubmit(true);
    const priorities = {
      selected: selectedPriorities,
      other: otherText,
    }
    console.log("User priorities submitted:", priorities);
    
    try {
      const leadUpdateResult = await updateLeadWithPriorities({
        quizId: quizId,
        priorities: priorities
      });
      console.log("Lead priorities updated:", leadUpdateResult);
      
      // Navigate to a success page or back to results
      resultsData.leadId = leadUpdateResult.id;
      // navigate('/quiz/specific/results', { state: { ...resultsData, from: location.pathname } });
    } catch (error) {
      console.error("Error updating priorities:", error);
      // Handle error appropriately
    }
  };

  const renderPriorityButton = (priority: { name: string; icon: React.FC<any> }) => {
    const isSelected = selectedPriorities.includes(priority.name);
    const IconComponent = priority.icon;

    return (
      <Pressable
        key={priority.name}
        onPress={() => handleSelectPriority(priority.name)}
        style={{
          backgroundColor: isSelected ? colors.gray[300] : colors.gray[100],
          borderRadius: 12,
          padding: spacing.lg,
          width: isMobile ? '100%' : 'calc(50% - 12px)',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderWidth: 1,
          borderColor: isSelected ? colors.gray[700] : colors.gray[100],
          transition: 'all 0.2s',
          cursor: 'pointer',
        }}
      >
        <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: spacing.md }}>
          <IconComponent color={colors.dark} width={30} height={30} />
          <CustomText style={{ fontSize: isMobile ? 18 : 24, fontWeight: '500', fontFamily: "Nunito", color: colors.dark }}>
            {priority.name}
          </CustomText>
        </View>
        <View
          style={{
            width: 20,
            height: 20,
            borderRadius: 999,
            borderWidth: 2,
            borderColor: isSelected ? colors.gray[600] : colors.gray[300],
            backgroundColor: isSelected ? colors.gray[600] : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        />
      </Pressable>
    );
  };

  return (
    <View style={{ 
      width: '100%', 
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: isMobile ? spacing.md : spacing.xl,
    }}>
      <View
        style={{
          backgroundColor: colors.white,
          borderRadius: 16,
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
          padding: isMobile ? spacing.lg : spacing.xxl,
          maxWidth: 900,
          width: "100%",
        }}
      >
        {!hasSubmit && (
          <Pressable
          onPress={handleBack}
          style={{
            padding: spacing.sm,
            cursor: 'pointer',
            position: 'absolute',
            top: isMobile ? spacing.md : spacing.lg,
            left: isMobile ? spacing.md : spacing.lg,
            zIndex: 1,
            }}
          >
            <View style={{ transform: 'rotate(90deg)' }}>
              <SvgChevron color={colors.gray[600]} width={24} height={24} />
            </View>
          </Pressable>
        )}
        
        {!hasSubmit ? (
        <View style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: spacing.xl,
        }}>
          <CustomText style={{
            fontSize: isMobile ? 36 : 48,
            fontWeight: '600',
            color: colors.dark,
            fontFamily: "Nunito",
            textAlign: 'center',
            marginTop: isMobile ? spacing.xl : 0,
          }}>
            Are there any specific areas you would like to cover?
          </CustomText>

          <View style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: spacing.lg,
            justifyContent: 'center',
            width: '100%',
          }}>
            {priorities.map(renderPriorityButton)}
          </View>

          <TextField
            label="Extra Notes"
            placeholder="I would like to focus on..."
            variant="outlined"
            fullWidth
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            color="primary"
            style={{ marginTop: spacing.lg, maxWidth: 'calc(100% - 24px)' }}
          />

          <Pressable 
            onPress={handleSubmit}
            style={({ hovered }: { hovered: boolean }) => ({
              backgroundColor: hovered ? '#515A64' : colors.dark,
              paddingVertical: isMobile ? spacing.sm : spacing.md,
              paddingHorizontal: isMobile ? spacing.xl : spacing.xxl,
              borderRadius: 12,
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '50%',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            })}
          >
            <CustomText style={{
              color: colors.white,
              fontSize: isMobile ? 18 : 24,
              fontWeight: '600',
              fontFamily: typography.fontFamily.body,
            }}>
              Submit
            </CustomText>
          </Pressable>
        </View>
        ) : (
          <View style={{marginTop: isMobile ? 30 : 20}}>
            <CustomText style={{
              fontFamily: "Nunito",
              fontWeight: 400,
              fontSize: isMobile ? 24 : 36,
              textAlign: 'center',
              marginBottom: isMobile ? 10 : 15,
            }}>
              Thank you for reaching out to us. You will be in contact soon.
            </CustomText>
            <Pressable 
              onPress={handleReturn}
              style={({ hovered }: { hovered: boolean }) => ({
                backgroundColor: hovered ? '#515A64' : colors.dark,
                paddingVertical: isMobile ? spacing.sm : spacing.md,
                paddingHorizontal: isMobile ? spacing.xl : spacing.xxl,
                borderRadius: 12,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              })}
            >
              <CustomText style={{
                color: colors.white,
                fontSize: isMobile ? 18 : 24,
                fontWeight: '600',
                fontFamily: typography.fontFamily.body,
              }}>
                Return to my results
              </CustomText>
            </Pressable>
          </View>
        )}
      </View>
    </View>
  );
}
